(() => {
  if (window.__motherDaySaleModularInitialized) {
    return;
  }

  window.__motherDaySaleModularInitialized = true;

  const copiedStateDuration = 5000;

  const copyText = async (value) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const fallbackField = document.createElement('textarea');
    fallbackField.value = value;
    fallbackField.setAttribute('readonly', '');
    fallbackField.style.position = 'fixed';
    fallbackField.style.opacity = '0';
    document.body.appendChild(fallbackField);
    fallbackField.select();
    document.execCommand('copy');
    fallbackField.remove();
  };

  const padNumber = (value) => String(value).padStart(2, '0');

  const updateCountdown = (countdown) => {
    const endValue = countdown.getAttribute('data-end');
    if (!endValue) {
      return;
    }

    const endTime = new Date(endValue).getTime();
    if (Number.isNaN(endTime)) {
      return;
    }

    const remaining = Math.max(0, endTime - Date.now());
    const parts = {
      days: Math.floor(remaining / 86400000),
      hours: padNumber(Math.floor((remaining % 86400000) / 3600000)),
      minutes: padNumber(Math.floor((remaining % 3600000) / 60000)),
      seconds: padNumber(Math.floor((remaining % 60000) / 1000)),
    };

    Object.keys(parts).forEach((key) => {
      const field = countdown.querySelector(`[data-${key}]`);
      if (field) {
        field.textContent = parts[key];
      }
    });

    if (remaining === 0) {
      countdown.classList.add('is-ended');
    }
  };

  const syncCountdowns = () => {
    document.querySelectorAll('[data-mother-countdown]').forEach(updateCountdown);
  };

  const initCountdowns = () => {
    if (!document.querySelector('[data-mother-countdown]')) {
      return;
    }

    syncCountdowns();

    if (!window.__motherDaySaleCountdownTimer) {
      window.__motherDaySaleCountdownTimer = window.setInterval(syncCountdowns, 1000);
    }
  };

  const getDragItems = (rail) => {
    const taggedItems = rail.querySelectorAll('[data-mother-drag-item]');
    if (taggedItems.length) {
      return [...taggedItems];
    }

    return [...rail.children];
  };

  const getDragStep = (rail) => {
    const items = getDragItems(rail);
    const firstCard = items[0];
    const secondCard = items[1];

    if (!firstCard) {
      return Math.max(rail.clientWidth * 0.75, 240);
    }

    if (secondCard) {
      return secondCard.getBoundingClientRect().left - firstCard.getBoundingClientRect().left;
    }

    return firstCard.getBoundingClientRect().width;
  };

  const initDragScroll = (root = document) => {
    const pointerFine = window.matchMedia('(pointer: fine)');
    const rails = root.querySelectorAll('[data-mother-drag-scroll]');

    rails.forEach((rail) => {
      if (rail.dataset.motherDragReady === 'true') {
        return;
      }

      rail.dataset.motherDragReady = 'true';
      rail.classList.toggle('is-grabbable', pointerFine.matches);

      let mouseDown = false;
      let mouseMoved = false;
      let dragDistance = 0;
      let startX = 0;
      let startScrollLeft = 0;
      let suppressClick = false;

      const clearDrag = () => {
        mouseDown = false;
        rail.classList.remove('is-dragging');

        window.requestAnimationFrame(() => {
          suppressClick = false;
        });
      };

      const handleMouseMove = (event) => {
        if (!mouseDown) {
          return;
        }

        const deltaX = event.pageX - startX;
        dragDistance = deltaX;

        if (!mouseMoved && Math.abs(deltaX) > 4) {
          mouseMoved = true;
          suppressClick = true;
        }

        if (!mouseMoved) {
          return;
        }

        event.preventDefault();
        rail.scrollLeft = startScrollLeft - deltaX;
      };

      const handleMouseUp = () => {
        if (!mouseDown) {
          return;
        }

        clearDrag();
      };

      rail.addEventListener('mousedown', (event) => {
        if (!pointerFine.matches || event.button !== 0) {
          return;
        }

        if (rail.scrollWidth <= rail.clientWidth + 2) {
          return;
        }

        mouseDown = true;
        mouseMoved = false;
        dragDistance = 0;
        startX = event.pageX;
        startScrollLeft = rail.scrollLeft;
        rail.classList.add('is-dragging');
        event.preventDefault();
      });

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      rail.addEventListener('mouseleave', handleMouseUp);

      rail.addEventListener(
        'click',
        (event) => {
          if (!suppressClick || Math.abs(dragDistance) <= 4) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      rail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
          return;
        }

        const direction = event.key === 'ArrowRight' ? 1 : -1;
        rail.scrollBy({
          left: getDragStep(rail) * direction,
          behavior: 'smooth',
        });
        event.preventDefault();
      });

      const handlePointerChange = (event) => {
        rail.classList.toggle('is-grabbable', event.matches);
      };

      if (typeof pointerFine.addEventListener === 'function') {
        pointerFine.addEventListener('change', handlePointerChange);
      } else if (typeof pointerFine.addListener === 'function') {
        pointerFine.addListener(handlePointerChange);
      }
    });
  };

  const updateScrollButtonState = (rail) => {
    const railId = rail.id;
    if (!railId) {
      return;
    }

    const prevButtons = document.querySelectorAll(`[data-mother-scroll-prev][data-mother-scroll-target="${railId}"]`);
    const nextButtons = document.querySelectorAll(`[data-mother-scroll-next][data-mother-scroll-target="${railId}"]`);
    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth - 1);
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft >= maxScrollLeft;

    prevButtons.forEach((button) => {
      button.disabled = atStart;
    });

    nextButtons.forEach((button) => {
      button.disabled = atEnd;
    });
  };

  const initScrollButtons = (root = document) => {
    const rails = root.querySelectorAll('[data-mother-drag-scroll][id]');

    rails.forEach((rail) => {
      if (rail.dataset.motherScrollButtonsReady === 'true') {
        updateScrollButtonState(rail);
        return;
      }

      rail.dataset.motherScrollButtonsReady = 'true';

      const railId = rail.id;
      const prevButtons = document.querySelectorAll(`[data-mother-scroll-prev][data-mother-scroll-target="${railId}"]`);
      const nextButtons = document.querySelectorAll(`[data-mother-scroll-next][data-mother-scroll-target="${railId}"]`);
      const handleButtonClick = (direction) => {
        rail.scrollBy({
          left: getDragStep(rail) * direction,
          behavior: 'smooth',
        });
      };

      prevButtons.forEach((button) => {
        button.addEventListener('click', () => {
          handleButtonClick(-1);
        });
      });

      nextButtons.forEach((button) => {
        button.addEventListener('click', () => {
          handleButtonClick(1);
        });
      });

      rail.addEventListener('scroll', () => {
        updateScrollButtonState(rail);
      }, { passive: true });

      updateScrollButtonState(rail);
    });

    const refreshButtonState = () => {
      document.querySelectorAll('[data-mother-drag-scroll][id]').forEach((rail) => {
        updateScrollButtonState(rail);
      });
    };

    if (!window.__motherDaySaleScrollButtonsResizeBound) {
      window.__motherDaySaleScrollButtonsResizeBound = true;
      window.addEventListener('resize', refreshButtonState);
    }
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-mother-copy]');
    if (!trigger) {
      return;
    }

    const value = trigger.getAttribute('data-mother-copy') || '';
    const label = trigger.querySelector('[data-copy-label]');
    const originalText = label ? label.textContent : trigger.textContent;
    const copiedText = trigger.getAttribute('data-copied-text') || 'COPIED!';

    try {
      await copyText(value);
    } catch (error) {
      // Ignore clipboard failures and still surface the copied state.
    }

    if (label) {
      label.textContent = copiedText;
    }

    trigger.classList.add('is-copied');

    window.setTimeout(() => {
      if (label) {
        label.textContent = originalText;
      }

      trigger.classList.remove('is-copied');
    }, copiedStateDuration);
  });

  const init = (root = document) => {
    initCountdowns();
    initDragScroll(root);
    initScrollButtons(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target || document);
  });

  if (document.readyState !== 'loading') {
    init(document);
  }
})();
